import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, inputClass, selectClass, channelOptions, relative, Avatar, asList } from './dashboardShared';

// The dashboard's Gatekeep tab: which servers TOGΞ may be in. In Approval mode, a server TOGΞ is added to waits (TOGΞ
// does nothing there, and posts nothing) until you approve or deny it here or from the DM TOGΞ sends you. Whoever added
// it is DMed that it's waiting, and DMed again (with a Partner Dashboard button) when it's approved. Deny: TOGΞ leaves,
// silently. CHΞΞKSCORD and servers you own are never left.

const MODES = [
  { id: 'approval', icon: '🔔', label: 'Approval', text: 'New servers wait for you to approve or deny them. TOGΞ does nothing there until then.' },
  { id: 'closed', icon: '🔒', label: 'Closed', text: 'TOGΞ leaves any new server straight away, silently. No requests, no DMs.' },
  { id: 'open', icon: '🔓', label: 'Open', text: 'Every server TOGΞ is added to is approved.' },
];

export default function GatekeepTab({ ask, setNote, failed }) {
  const [state, setState] = useState(null); // { mode, servers, requests, waiting, log, invite }
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState('');
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [copied, setCopied] = useState(false);
  const [channels, setChannels] = useState([]); // CHΞΞKSCORD's channels, for the Gatekeep channel

  const take = (answer) => {
    if (failed(answer)) return false;
    // Every list filled in (Firebase drops empty ones)
    setState({ ...answer.data, servers: asList(answer.data?.servers), requests: asList(answer.data?.requests),
      waiting: asList(answer.data?.waiting), log: asList(answer.data?.log) });
    if (answer.data?.message) setNote({ text: answer.data.message, ok: true });
    return true;
  };
  const load = useCallback(async () => take(await ask('gatekeep.state')), [ask]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    ask('channels').then(answer => { if (answer.ok) setChannels(asList(answer.data?.channels)); });
  }, [ask]);
  // Requests can arrive at any moment: look again every 30 seconds while the tab's open
  useEffect(() => {
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const act = async (verb, args, key, needsConfirm = false) => {
    if (needsConfirm && confirm !== key) { setConfirm(key); return false; }
    setConfirm(''); setBusy(key); setNote(null);
    const ok = take(await ask(`gatekeep.${verb}`, args));
    setBusy('');
    return ok;
  };

  if (!state) return <Card><p className="text-zinc-500 text-xs">Loading…</p></Card>;
  const mode = MODES.find(m => m.id === state.mode) || MODES[0];
  const strays = state.servers.filter(s => !s.allowed);

  return (
    <div className="space-y-4">
      <Card title="Mode">
        <div className="grid grid-cols-3 gap-1.5">
          {MODES.map(m => (
            <button key={m.id} type="button" disabled={!!busy || m.id === state.mode}
              onClick={() => act('mode', { mode: m.id }, `mode-${m.id}`)}
              className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-colors ${m.id === state.mode ? 'border-[#5865f2] bg-[#5865f2]/20 text-white' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>
              {m.icon} {m.label}
            </button>
          ))}
        </div>
        <p className="text-zinc-400 text-[11px] mt-2">{mode.text}</p>
      </Card>

      <Card title="Gatekeep channel">
        <p className="text-zinc-400 text-[11px] mb-2">
          A CHΞΞKSCORD channel where TOGΞ posts each join request (pinging you, with Approve and Deny), and any server it
          joins or leaves. Handy if your DMs are off. Keep it private: only you can press the buttons, but anyone who
          can see the channel can read the requests.
        </p>
        <select value={state.channel || ''} disabled={!!busy} onChange={e => act('channel', { channel: e.target.value }, 'channel')}
          className={`w-full ${selectClass}`}>
          <option value="">No channel (DMs only)</option>
          {channelOptions(channels)}
        </select>
      </Card>

      <Card title={`Join requests · ${state.requests.length}`}>
        {state.requests.length === 0 ? <p className="text-zinc-500 text-xs">No servers are waiting for approval.</p> : (
          <ul className="divide-y divide-zinc-800">
            {state.requests.map(r => (
              <li key={r.id} className="flex items-center gap-2 py-2 flex-wrap">
                <Avatar src={r.icon} name={r.name} size={32} />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-semibold truncate">{r.name} <span className="text-zinc-600 font-normal">({r.id})</span></p>
                  <p className="text-zinc-500 text-[11px] truncate">
                    {(r.members || 0).toLocaleString()} members · owner {r.owner || 'unknown'} · added by {r.by || 'unknown'}{r.partner ? ' (🤝 partner)' : ''} {relative(r.at)}
                  </p>
                  <p className="text-zinc-600 text-[10px]">
                    {r.told ? 'They were DMed that it’s waiting.' : 'They couldn’t be DMed.'} Left automatically {relative(r.expiresAt)} if not answered.
                  </p>
                </div>
                <Button small tone="success" disabled={!!busy} onClick={() => act('approve', { id: r.id }, `approve-${r.id}`)}>
                  {busy === `approve-${r.id}` ? '…' : '✅ Approve'}
                </Button>
                <Button small tone={confirm === `deny-${r.id}` ? 'danger' : 'ghost'} disabled={!!busy} onClick={() => act('deny', { id: r.id }, `deny-${r.id}`, true)}>
                  {busy === `deny-${r.id}` ? '…' : confirm === `deny-${r.id}` ? 'Yes, leave it' : '⛔ Deny'}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-zinc-600 text-[10px] mt-2">
          Each request is also DMed to you{state.channel ? ' and posted in the Gatekeep channel' : ''}, with the same Approve and Deny buttons.
        </p>
      </Card>

      {strays.length > 0 && (
        <p className="text-amber-300 text-xs rounded-lg border border-amber-700/50 bg-amber-950/20 px-3 py-2">
          TOGΞ is in {strays.length} server{strays.length !== 1 ? 's' : ''} that {strays.length !== 1 ? "aren't" : "isn't"} approved,
          so it does nothing there. Approve or leave {strays.length !== 1 ? 'them' : 'it'} below.
        </p>
      )}

      <Card title={`Servers TOGΞ is in · ${state.servers.length}`}>
        <ul className="divide-y divide-zinc-800">
          {state.servers.map(s => (
            <li key={s.id} className="flex items-center gap-2 py-2">
              <Avatar src={s.icon} name={s.name} size={32} />
              <div className="min-w-0 flex-1">
                <p className="text-white text-xs font-semibold truncate">
                  {s.name}
                  {s.home && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#5865f2]/30 text-[#c9cdfb]">HOME</span>}
                  {s.yours && !s.home && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-zinc-700 text-zinc-200">YOURS</span>}
                  {!s.allowed && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-900/50 text-red-300">NOT APPROVED</span>}
                </p>
                <p className="text-zinc-500 text-[11px] truncate">
                  {(s.members || 0).toLocaleString()} members · owner {s.owner}{s.joinedAt ? ` · joined ${relative(s.joinedAt)}` : ''}
                </p>
              </div>
              {s.protected ? (
                <span className="text-zinc-600 text-[10px] text-right">Always allowed</span>
              ) : (
                <>
                  <Button small tone={s.allowed ? 'ghost' : 'success'} disabled={!!busy}
                    onClick={() => act('allow', { id: s.id, allowed: !s.allowed }, `allow-${s.id}`)}>
                    {busy === `allow-${s.id}` ? '…' : s.allowed ? 'Unapprove' : 'Approve'}
                  </Button>
                  <Button small tone={confirm === `leave-${s.id}` ? 'danger' : 'ghost'} disabled={!!busy}
                    onClick={() => act('leave', { id: s.id }, `leave-${s.id}`, true)}>
                    {busy === `leave-${s.id}` ? '…' : confirm === `leave-${s.id}` ? 'Sure?' : 'Leave'}
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
        <p className="text-zinc-600 text-[10px] mt-2">CHΞΞKSCORD and servers you own are never left. Leaving is always silent.</p>
      </Card>

      <Card title="Adding TOGΞ to a server">
        <div className="space-y-2">
          <p className="text-zinc-400 text-[11px]">
            Send someone the invite link: when they add TOGΞ, you get a request to approve. (They can only add TOGΞ while
            Public Bot is on in the Discord Developer Portal; you always can.) The link asks only for what partner servers
            need, not Administrator.
          </p>
          {state.invite && (
            <div className="flex items-center gap-2 flex-wrap">
              <a href={state.invite} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-md bg-[#5865f2] hover:bg-[#4752c4] text-white text-xs font-semibold">Invite TOGΞ ↗</a>
              <button type="button" onClick={() => navigator.clipboard?.writeText(state.invite).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {})}
                className="px-2 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px]">{copied ? 'Copied ✓' : 'Copy link'}</button>
            </div>
          )}
          <p className="text-zinc-500 text-[11px] pt-2">Or approve a server in advance, so it's ready the moment TOGΞ is added (no request):</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={newId} onChange={e => setNewId(e.target.value.trim())} placeholder="Server ID" className={`sm:w-52 ${inputClass}`} />
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name, to recognise it (optional)" className={`flex-1 ${inputClass}`} />
            <Button disabled={!newId || !!busy}
              onClick={async () => { if (await act('allow', { id: newId, allowed: true, name: newName }, 'pre-allow')) { setNewId(''); setNewName(''); } }}>
              {busy === 'pre-allow' ? '…' : 'Approve in advance'}
            </Button>
          </div>
          {state.waiting.length > 0 && (
            <ul className="space-y-1">
              {state.waiting.map(w => (
                <li key={w.id} className="flex items-center gap-2 text-[11px] text-zinc-300">
                  <span className="flex-1 min-w-0 truncate">✅ {w.name} <span className="text-zinc-600">({w.id}) · approved, TOGΞ isn't in it yet</span></span>
                  <Button small tone="ghost" disabled={!!busy} onClick={() => act('allow', { id: w.id, allowed: false }, `allow-${w.id}`)}>Remove</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      <Card title="Recent joins and leaves">
        {state.log.length === 0 ? <p className="text-zinc-500 text-xs">Nothing yet.</p> : (
          <ul className="space-y-1.5">
            {state.log.map((e, i) => (
              <li key={i} className="text-[11px]">
                <span className="text-white font-semibold">{e.name}</span>
                <span className="text-zinc-500"> ({e.id}, {e.members} members) · {relative(e.at * 1000)}</span>
                <p className="text-zinc-400">{e.action}{e.by ? ` · added by ${e.by}` : ''}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
